import { useEffect, useRef, useState } from 'react'

interface DatePickerProps {
	value: string // UTC timestamp or date string
	onChange: (date: string) => void // Will receive UTC timestamp
	placeholder?: string
	min?: string
	max?: string
	disabled?: boolean
	allowPastDates?: boolean
}

function formatDisplayDate(date: Date): string {
	return date.toLocaleDateString('en-us', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

function parseUTCStamp(timestamp: string): Date {
	const date = new Date(timestamp)
	// Use UTC components to preserve the originally selected date
	// This avoids timezone shift when displaying the date
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

function formatDateForInput(date: Date): string {
	// Format as YYYY-MM-DD using local date values
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${year}-${month}-${day}`
}

export function DatePicker({
	value,
	onChange,
	placeholder = 'Select date',
	min,
	max,
	disabled = false,
	allowPastDates = false,
}: DatePickerProps) {
	const inputRef = useRef<HTMLInputElement>(null)
	const [date, setDate] = useState<Date>()

	useEffect(() => {
		if (value) {
			setDate(parseUTCStamp(value))
		} else {
			setDate(undefined)
		}
	}, [value])

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedDate = event.target.value // YYYY-MM-DD format
		if (selectedDate) {
			// Create a date at midnight local time
			const [year, month, day] = selectedDate.split('-').map(Number)
			const localMidnight = new Date(year, month - 1, day, 0, 0, 0, 0)

			// Send the UTC equivalent of local midnight
			// This preserves the selected date but converts it to UTC time
			onChange(localMidnight.toISOString())
		} else {
			onChange('')
		}
	}

	const handleButtonClick = () => {
		if (!disabled && inputRef.current) {
			inputRef.current.showPicker()
		}
	}

	const getMinDate = () => {
		if (min) return min
		if (!allowPastDates) return new Date().toISOString().split('T')[0]
		return undefined
	}

	return (
		<div className='relative'>
			<button
				type='button'
				onClick={handleButtonClick}
				disabled={disabled}
				className={`w-full flex items-center gap-2 p-2 rounded border transition-colors ${
					disabled
						? 'border-neutral-200 bg-neutral-50 cursor-not-allowed'
						: 'border-neutral-200 hover:border-primary-500'
				}`}>
				<div className='flex items-center justify-center w-8 h-8 rounded border border-neutral-100'>
					<svg
						className='w-4 h-4 text-neutral-600'
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'
						aria-hidden='true'>
						<rect x='3' y='4' width='18' height='18' rx='2' ry='2'></rect>
						<line x1='16' y1='2' x2='16' y2='6'></line>
						<line x1='8' y1='2' x2='8' y2='6'></line>
						<line x1='3' y1='10' x2='21' y2='10'></line>
					</svg>
				</div>
				<div
					className={`text-sm flex-1 text-left ${
						disabled ? 'text-neutral-400' : value ? 'text-neutral-900' : 'text-neutral-600'
					}`}>
					{date ? formatDisplayDate(date) : placeholder}
				</div>
			</button>
			<input
				ref={inputRef}
				type='date'
				value={date ? formatDateForInput(date) : ''}
				onChange={handleChange}
				min={getMinDate()}
				max={max}
				disabled={disabled}
				className='absolute inset-0 opacity-0 pointer-events-none'
				style={{
					colorScheme: 'light',
				}}
			/>
		</div>
	)
}
